import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BootSplash } from '../../components/Chloromance/BootSplash';
import { PlantFormDrawer } from '../../components/Chloromance/PlantFormDrawer';
import { PlantGrid } from '../../components/Chloromance/PlantGrid';
import { SuccessModal } from '../../components/Chloromance/SuccessModal';
import { SummaryBar } from '../../components/Chloromance/SummaryBar';
import { TrackerHero } from '../../components/Chloromance/TrackerHero';
import { WaterTodaySection } from '../../components/Chloromance/WaterTodaySection';
import { WeatherPanel } from '../../components/Chloromance/WeatherPanel';
import { ZoneToolbar } from '../../components/Chloromance/ZoneToolbar';
import { deleteUserPlant, loadUserPlants, loadUserTier, redeemUpgradeCoupon, saveUserPlant, saveUserPlants, saveUserTier } from '../../lib/chloromancePlants';
import { EMPTY_FORM, THEME_KEY, REMINDERS_KEY, WEATHER_LOCATION_KEY, DEFAULT_ZONES, DEFAULT_PLANT_TIER_ID, NOTIFIED_KEY, PLANT_LIMIT_NOTE, PLANT_TIERS } from '../../utils/Chloromance/constants';
import { ChloromanceTierId, ChloromanceTrackerProps, Plant, SuccessCelebration, PlantForm, WeatherLocation, WeatherSnapshot, HydrationState } from '../../utils/Chloromance/types';
import { PLANT_SAMPLES, applySampleToForm } from '../../utils/Chloromance/utils/catalogue';
import { cancelPlantReminderList, cancelPlantReminders, requestReminderPermission, schedulePlantReminderList, supportsNativeNotifications } from '../../utils/Chloromance/utils/notifications';
import { isPlant, normalizePlant, getPlantStatus, buildSummary, calculateWiltPower, buildCompanionMatches, createPlantId, getPlantCount } from '../../utils/Chloromance/utils/plants';
import { readPlantsForUser, getInitialChloromanceTheme, getStoredReminderPreference, getStoredWeatherLocation, readNotifiedStates, persistPlants, getUserStorageKey, hasStoredPlantsForUser } from '../../utils/Chloromance/utils/storage';
import { fetchOpenMeteoWeather } from '../../utils/Chloromance/utils/weather';
import './ChloromanceTracker.css';
import './../../utils/Chloromance/PlantCatalogue.css';
import './../../utils/Chloromance/PlantMobileSections.css';

type WateringUndo = {
  plants: Pick<Plant, 'id' | 'name' | 'last_watered_timestamp'>[];
};

function ChloromanceTracker({ userId = 'local', onOpenPrivacy, sessionBar, allowMockUpgrade = false }: ChloromanceTrackerProps) {
  const [plants, setPlants] = useState<Plant[]>(() => readPlantsForUser(userId));
  const [theme, setTheme] = useState(() => getInitialChloromanceTheme(userId));
  const [bootSplashVisible, setBootSplashVisible] = useState(true);
  const [successCelebration, setSuccessCelebration] = useState<SuccessCelebration | null>(null);
  const [now, setNow] = useState(Date.now());
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<PlantForm>(EMPTY_FORM);
  const [editingPlant, setEditingPlant] = useState<Plant | null>(null);
  const [selectedZone, setSelectedZone] = useState('All Locations');
  const [remindersEnabled, setRemindersEnabled] = useState(() => getStoredReminderPreference(userId));
  const [notificationMessage, setNotificationMessage] = useState('');
  const [weatherLocation, setWeatherLocation] = useState<WeatherLocation>(() => getStoredWeatherLocation(userId));
  const [weather, setWeather] = useState<WeatherSnapshot | null>(null);
  const [weatherMessage, setWeatherMessage] = useState('');
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [wateredPlantIds, setWateredPlantIds] = useState<string[]>([]);
  const [wateringUndo, setWateringUndo] = useState<WateringUndo | null>(null);
  const [plantLimitMessage, setPlantLimitMessage] = useState('');
  const [cloudSyncMessage, setCloudSyncMessage] = useState('');
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [activeTierId, setActiveTierId] = useState<ChloromanceTierId>(DEFAULT_PLANT_TIER_ID);
  const [selectedUpgradeTierId, setSelectedUpgradeTierId] = useState<ChloromanceTierId>('grower');
  const [couponCode, setCouponCode] = useState('');
  const [couponMessage, setCouponMessage] = useState('');
  const [couponRedeeming, setCouponRedeeming] = useState(false);
  const notifiedStates = useRef<Record<string, HydrationState>>(readNotifiedStates(userId));
  const celebrationTimer = useRef<number | null>(null);

  useEffect(() => persistPlants(userId, plants), [plants, userId]);
  useEffect(() => window.localStorage.setItem(getUserStorageKey(THEME_KEY, userId), theme), [theme, userId]);
  useEffect(() => {
    window.localStorage.setItem(getUserStorageKey(REMINDERS_KEY, userId), remindersEnabled ? 'enabled' : 'disabled');
  }, [remindersEnabled, userId]);
  useEffect(() => {
    window.localStorage.setItem(getUserStorageKey(WEATHER_LOCATION_KEY, userId), JSON.stringify(weatherLocation));
  }, [weatherLocation, userId]);

  useEffect(() => {
    let cancelled = false;
    const localPlants = readPlantsForUser(userId);
    const hasLocalPlants = hasStoredPlantsForUser(userId);
    setCloudSyncMessage('');
    loadUserPlants(userId)
      .then((cloudPlants) => {
        if (cancelled) return;
        if (cloudPlants.length > 0) {
          const normalizedCloudPlants = cloudPlants.filter(isPlant).map(normalizePlant);
          setPlants(normalizedCloudPlants);
          persistPlants(userId, normalizedCloudPlants);
          return;
        }
        if (hasLocalPlants && localPlants.length > 0) {
          saveUserPlants(userId, localPlants).catch(() => {
            if (!cancelled) setCloudSyncMessage('Saved locally. Cloud backup could not sync yet.');
          });
        }
      })
      .catch(() => {
        if (!cancelled) setCloudSyncMessage('Saved locally. Cloud backup could not load yet.');
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    loadUserTier(userId)
      .then((tierId) => {
        if (!cancelled) setActiveTierId(tierId);
      })
      .catch(() => {
        if (!cancelled) setCloudSyncMessage('Saved locally. Account tier could not load yet.');
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    const ticker = window.setInterval(() => setNow(Date.now()), 10000);
    return () => window.clearInterval(ticker);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setBootSplashVisible(false), 1800);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => () => {
    if (celebrationTimer.current) window.clearTimeout(celebrationTimer.current);
  }, []);

  const loadWeather = useCallback(async (location: WeatherLocation) => {
    if (process.env.NODE_ENV === 'test' || typeof fetch === 'undefined') return;
    const latitude = Number(location.latitude);
    const longitude = Number(location.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      setWeatherMessage('Enter valid latitude and longitude values.');
      return;
    }
    setWeatherLoading(true);
    setWeatherMessage('');
    try {
      setWeather(await fetchOpenMeteoWeather(latitude, longitude));
    } catch (error) {
      setWeather(null);
      setWeatherMessage(error instanceof Error ? error.message : 'Could not fetch weather.');
    } finally {
      setWeatherLoading(false);
      window.setTimeout(() => setBootSplashVisible(false), 450);
    }
  }, []);

  useEffect(() => {
    loadWeather(getStoredWeatherLocation(userId));
  }, [loadWeather, userId]);

  useEffect(() => {
    const weatherRefresh = window.setInterval(() => loadWeather(weatherLocation), 30 * 60 * 1000);
    return () => window.clearInterval(weatherRefresh);
  }, [loadWeather, weatherLocation]);

  useEffect(() => {
    if (!remindersEnabled || !supportsNativeNotifications()) return;

    schedulePlantReminderList(plants).catch(() => {
      setNotificationMessage('Native reminders could not be scheduled.');
    });
  }, [plants, remindersEnabled]);

  const zones = useMemo(
    () => Array.from(new Set([...DEFAULT_ZONES, ...plants.map((plant) => plant.chloromance_zone)])).sort(),
    [plants]
  );
  const activeZones = useMemo(() => Array.from(new Set(plants.map((plant) => plant.chloromance_zone))).sort(), [plants]);
  const weatherMultiplier = weather?.multiplier || 1;
  const plantStatuses = useMemo(
    () => plants.reduce<Record<string, ReturnType<typeof getPlantStatus>>>((statuses, plant) => {
      statuses[plant.id] = getPlantStatus(plant, now, weatherMultiplier);
      return statuses;
    }, {}),
    [plants, now, weatherMultiplier]
  );
  const summary = useMemo(() => buildSummary(plants, plantStatuses), [plants, plantStatuses]);
  const wiltPower = useMemo(() => calculateWiltPower(plants, plantStatuses), [plants, plantStatuses]);
  const visiblePlants = useMemo(
    () => selectedZone === 'All Locations' ? plants : plants.filter((plant) => plant.chloromance_zone === selectedZone),
    [plants, selectedZone]
  );
  const companionMatches = useMemo(() => buildCompanionMatches(plants), [plants]);
  const activeTier = PLANT_TIERS.find((tier) => tier.id === activeTierId) || PLANT_TIERS[0];
  const selectedUpgradeTier = PLANT_TIERS.find((tier) => tier.id === selectedUpgradeTierId) || PLANT_TIERS[1];
  const plantCount = useMemo(() => plants.reduce((total, plant) => total + getPlantCount(plant), 0), [plants]);
  const plantLimitReached = activeTier.plantLimit !== null && plantCount >= activeTier.plantLimit;
  const undoWateringLabel = useMemo(() => {
    if (!wateringUndo) return '';
    if (wateringUndo.plants.length === 1) return `Undo ${wateringUndo.plants[0].name}`;
    return `Undo ${wateringUndo.plants.length} plants`;
  }, [wateringUndo]);

  const plantToForm = (plant: Plant): PlantForm => ({
    name: plant.name,
    variety: plant.variety,
    plant_type: plant.plant_type || 'Other',
    plant_count: String(getPlantCount(plant)),
    chloromance_zone: plant.chloromance_zone,
    watering_frequency_days: String(plant.watering_frequency_days),
    note: plant.note || '',
    sample_id: plant.catalogue_id || '',
    catalogue_type: plant.catalogue_type || '',
    suggested_location: plant.suggested_location || '',
    fruit_or_flower_months: plant.fruit_or_flower_months || [],
    best_harvest_months: plant.best_harvest_months || [],
    care_profile: plant.care_profile || null,
  });

  useEffect(() => {
    if (
      supportsNativeNotifications() ||
      !remindersEnabled ||
      !('Notification' in window) ||
      Notification.permission !== 'granted'
    ) {
      return;
    }
    let changed = false;
    plants.forEach((plant) => {
      const state = plantStatuses[plant.id].state;
      const previousState = notifiedStates.current[plant.id];
      if (state === 'Optimal') {
        if (previousState) {
          delete notifiedStates.current[plant.id];
          changed = true;
        }
        return;
      }
      if (previousState !== state) {
        new Notification(state === 'Critical' ? 'WiltPower critical' : 'WiltPower draining', {
          body: `Your ${plant.name} in ${plant.chloromance_zone} needs watering: ${plantStatuses[plant.id].nextWateringText}.`,
          tag: `chloromance-${plant.id}-${state}`,
        });
        notifiedStates.current[plant.id] = state;
        changed = true;
      }
    });
    if (changed) {
      window.localStorage.setItem(getUserStorageKey(NOTIFIED_KEY, userId), JSON.stringify(notifiedStates.current));
    }
  }, [plants, plantStatuses, remindersEnabled, userId]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsedPlantCount = Number(form.plant_count);
    const nextPlantCount = Number.isFinite(parsedPlantCount) ? Math.max(1, Math.round(parsedPlantCount)) : 1;
    const currentPlantCount = editingPlant ? getPlantCount(editingPlant) : 0;
    const nextTotalPlantCount = plantCount - currentPlantCount + nextPlantCount;
    if (activeTier.plantLimit !== null && nextTotalPlantCount > activeTier.plantLimit) {
      setPlantLimitMessage(PLANT_LIMIT_NOTE);
      return;
    }
    const nextPlant: Plant = {
      id: editingPlant?.id || createPlantId(),
      name: form.name.trim(),
      variety: form.variety.trim(),
      plant_type: form.plant_type,
      plant_count: nextPlantCount,
      chloromance_zone: form.chloromance_zone.trim() || DEFAULT_ZONES[0],
      watering_frequency_days: Math.max(1, Math.round(Number(form.watering_frequency_days))),
      last_watered_timestamp: editingPlant?.last_watered_timestamp || Date.now(),
      note: form.note.trim() || undefined,
      catalogue_id: form.sample_id || undefined,
      catalogue_type: form.catalogue_type || undefined,
      suggested_location: form.suggested_location.trim() || undefined,
      fruit_or_flower_months: form.fruit_or_flower_months.length ? form.fruit_or_flower_months : undefined,
      best_harvest_months: form.best_harvest_months.length ? form.best_harvest_months : undefined,
      care_profile: form.care_profile || undefined,
    };
    if (!nextPlant.name || !nextPlant.variety) return;

    setPlants((currentPlants) => (
      editingPlant
        ? currentPlants.map((plant) => plant.id === editingPlant.id ? nextPlant : plant)
        : [nextPlant, ...currentPlants]
    ));
    saveUserPlant(userId, nextPlant)
      .then(() => setCloudSyncMessage(''))
      .catch(() => setCloudSyncMessage('Saved locally. Cloud backup could not sync yet.'));
    if (!editingPlant) setSelectedZone('All Locations');
    setPlantLimitMessage('');
    setForm(EMPTY_FORM);
    setEditingPlant(null);
    setFormOpen(false);
  };

  const openPlantForm = () => {
    if (plantLimitReached) { 
      setPlantLimitMessage(PLANT_LIMIT_NOTE);
      return;
    }
    setPlantLimitMessage('');
    setEditingPlant(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const editPlant = (plant: Plant) => {
    setPlantLimitMessage('');
    setEditingPlant(plant);
    setForm(plantToForm(plant));
    setFormOpen(true);
  };

  const closePlantForm = () => {
    setFormOpen(false);
    setEditingPlant(null);
    setForm(EMPTY_FORM);
  };

  const showSuccessCelebration = useCallback(() => {
    setSuccessCelebration({ title: 'Task Complete!', message: 'Your plants are Rooting For You!' });
    if (celebrationTimer.current) window.clearTimeout(celebrationTimer.current);
    celebrationTimer.current = window.setTimeout(() => setSuccessCelebration(null), 2600);
  }, []);

  const markWatered = useCallback((plantIds: string[], celebrate = false) => {
    const undoPlants = plants
      .filter((plant) => plantIds.includes(plant.id))
      .map((plant) => ({
        id: plant.id,
        name: plant.name,
        last_watered_timestamp: plant.last_watered_timestamp,
      }));
    if (undoPlants.length === 0) return;

    const rescuedCriticalPlant = plants.some(
      (plant) => plantIds.includes(plant.id) && plantStatuses[plant.id].state === 'Critical'
    );
    const timestamp = Date.now();
    const nextPlants = plants.map((plant) => plantIds.includes(plant.id) ? { ...plant, last_watered_timestamp: timestamp } : plant);
    const wateredPlants = nextPlants.filter((plant) => plantIds.includes(plant.id));
    setPlants(nextPlants);
    saveUserPlants(userId, wateredPlants)
      .then(() => setCloudSyncMessage(''))
      .catch(() => setCloudSyncMessage('Saved locally. Cloud backup could not sync yet.'));
    setWateringUndo({ plants: undoPlants });
    plantIds.forEach((plantId) => delete notifiedStates.current[plantId]);
    window.localStorage.setItem(getUserStorageKey(NOTIFIED_KEY, userId), JSON.stringify(notifiedStates.current));
    setWateredPlantIds(plantIds);
    window.setTimeout(() => {
      setWateredPlantIds((currentIds) => currentIds.filter((id) => !plantIds.includes(id)));
    }, 900);
    if (celebrate || rescuedCriticalPlant) showSuccessCelebration();
  }, [plants, plantStatuses, showSuccessCelebration, userId]);

  const undoLastWatering = useCallback(() => {
    if (!wateringUndo) return;
    const undoByPlantId = new Map(wateringUndo.plants.map((plant) => [plant.id, plant]));
    const restoredPlants = plants.map((plant) => {
      const undoPlant = undoByPlantId.get(plant.id);
      return undoPlant ? { ...plant, last_watered_timestamp: undoPlant.last_watered_timestamp } : plant;
    });
    const syncedPlants = restoredPlants.filter((plant) => undoByPlantId.has(plant.id));

    setPlants(restoredPlants);
    setWateringUndo(null);
    setWateredPlantIds((currentIds) => currentIds.filter((id) => !undoByPlantId.has(id)));
    if (syncedPlants.length > 0) {
      saveUserPlants(userId, syncedPlants)
        .then(() => setCloudSyncMessage(''))
        .catch(() => setCloudSyncMessage('Restored locally. Cloud backup could not sync yet.'));
    }
  }, [plants, wateringUndo, userId]);

  const deletePlant = (plant: Plant) => {
    if (!window.confirm(`Delete ${plant.name} from Chloromance?`)) return;
    setPlants((currentPlants) => currentPlants.filter((currentPlant) => currentPlant.id !== plant.id));
    deleteUserPlant(userId, plant.id)
      .then(() => setCloudSyncMessage(''))
      .catch(() => setCloudSyncMessage('Deleted locally. Cloud backup could not sync yet.'));
    delete notifiedStates.current[plant.id];
    window.localStorage.setItem(getUserStorageKey(NOTIFIED_KEY, userId), JSON.stringify(notifiedStates.current));
    cancelPlantReminders(plant.id).catch(() => {
      setNotificationMessage('Native reminders could not be cleared.');
    });
    setWateredPlantIds((currentIds) => currentIds.filter((id) => id !== plant.id));
    setPlantLimitMessage('');
  };

  const toggleReminders = async () => {
    if (remindersEnabled) {
      setRemindersEnabled(false);
      setNotificationMessage('Reminders paused.');
      cancelPlantReminderList(plants).catch(() => {
        setNotificationMessage('Native reminders could not be cleared.');
      });
      return;
    }
    const permission = await requestReminderPermission();
    if (permission === 'unsupported') {
      setNotificationMessage('Native notifications are not supported in this browser.');
      return;
    }
    setRemindersEnabled(permission === 'granted');
    if (permission === 'granted') {
      schedulePlantReminderList(plants).catch(() => {
        setNotificationMessage('Native reminders could not be scheduled.');
      });
    }
    setNotificationMessage(
      permission === 'granted'
        ? 'Reminders enabled for thirsty and critical transitions.'
        : 'Notification permission was not granted.'
    );
  };

  const useCurrentLocation = () => {
    if (!('geolocation' in navigator)) {
      setWeatherMessage('Geolocation is not available in this browser.');
      return;
    }
    setWeatherLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextLocation = {
          latitude: position.coords.latitude.toFixed(4),
          longitude: position.coords.longitude.toFixed(4),
        };
        setWeatherLocation(nextLocation);
        loadWeather(nextLocation);
      },
      () => {
        setWeatherLoading(false);
        setWeatherMessage('Location permission was not granted.');
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const redeemCoupon = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCouponRedeeming(true);
    setCouponMessage('');
    try {
      const redeemedTierId = await redeemUpgradeCoupon(couponCode);
      const redeemedTier = PLANT_TIERS.find((tier) => tier.id === redeemedTierId) || PLANT_TIERS[0];
      setActiveTierId(redeemedTier.id);
      setSelectedUpgradeTierId(redeemedTier.id);
      setPlantLimitMessage('');
      setCouponCode('');
      setUpgradeOpen(false);
      setUpgradeMessage(`${redeemedTier.name} tier unlocked with coupon.`);
    } catch (error) {
      setCouponMessage(error instanceof Error ? error.message : 'Coupon could not be redeemed.');
    } finally {
      setCouponRedeeming(false);
    }
  };

  return (
    <main className="chloromance-shell" data-theme={theme}>
      {sessionBar}
      <TrackerHero
        theme={theme}
        remindersEnabled={remindersEnabled}
        notificationMessage={notificationMessage}
        plantLimitMessage={plantLimitMessage}
        cloudSyncMessage={upgradeMessage || cloudSyncMessage}
        allowMockUpgrade={allowMockUpgrade}
        onToggleTheme={() => setTheme((value) => value === 'dark' ? 'light' : 'dark')}
        onAddPlant={openPlantForm}
        onOpenMockUpgrade={() => setUpgradeOpen(true)}
        onToggleReminders={toggleReminders}
        onOpenPrivacy={onOpenPrivacy}
      />
      {bootSplashVisible && <BootSplash />}
      <WaterTodaySection plants={plants} statuses={plantStatuses} onWater={markWatered} />
      <SummaryBar wiltPower={wiltPower} plantCount={plantCount} activeTier={activeTier} summary={summary} />
      <WeatherPanel
        weather={weather}
        weatherMultiplier={weatherMultiplier}
        weatherLocation={weatherLocation}
        weatherMessage={weatherMessage}
        weatherLoading={weatherLoading}
        onLocationChange={setWeatherLocation}
        onRefresh={() => loadWeather(weatherLocation)}
        onUseCurrentLocation={useCurrentLocation}
      />
      <ZoneToolbar
        activeZones={activeZones}
        selectedZone={selectedZone}
        undoWateringLabel={undoWateringLabel}
        onSelectZone={setSelectedZone}
        onMassWaterZone={(zone) => {
          const plantIds = plants.filter((plant) => plant.chloromance_zone === zone).map((plant) => plant.id);
          markWatered(plantIds, plantIds.length > 0);
        }}
        onUndoWatering={undoLastWatering}
      />
      <PlantGrid
        plants={visiblePlants}
        selectedZone={selectedZone}
        statuses={plantStatuses}
        companionMatches={companionMatches}
        wateredPlantIds={wateredPlantIds}
        onWater={(plantId) => markWatered([plantId])}
        onEdit={editPlant}
        onDelete={deletePlant}
      />
      {formOpen && (
        <PlantFormDrawer
          form={form}
          zones={zones}
          samples={PLANT_SAMPLES}
          onChange={setForm}
          onSelectSample={(sampleId) => setForm((currentForm) => applySampleToForm(sampleId, currentForm))}
          onClose={closePlantForm}
          onSubmit={handleSubmit}
          isEditing={Boolean(editingPlant)}
        />
      )}
      {allowMockUpgrade && upgradeOpen && (
        <div className="upgrade-backdrop" role="presentation" onMouseDown={() => setUpgradeOpen(false)}>
          <aside className="upgrade-modal" role="dialog" aria-modal="true" aria-labelledby="upgrade-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="upgrade-modal-header">
              <div>
                <p>Web testing flow</p>
                <h2 id="upgrade-title">Mock upgrade</h2>
              </div>
              <button type="button" onClick={() => setUpgradeOpen(false)} aria-label="Close upgrade flow">×</button>
            </div>
            <div className="upgrade-plan-grid">
              {PLANT_TIERS.map((tier) => {
                const isSelected = tier.id === selectedUpgradeTierId;
                const isCurrent = tier.id === activeTierId;
                const plantLimit = tier.plantLimit === null ? 'Unlimited plants' : `${tier.plantLimit} plants`;

                return (
                  <button
                    key={tier.id}
                    type="button"
                    className={isSelected ? 'is-featured' : ''}
                    aria-pressed={isSelected}
                    onClick={() => setSelectedUpgradeTierId(tier.id)}
                  >
                    <span>{tier.name}{isCurrent ? ' · Current' : ''}</span>
                    <strong>{plantLimit}</strong>
                    <small>{tier.description}</small>
                  </button>
                );
              })}
            </div>
            <form className="coupon-form" onSubmit={redeemCoupon}>
              <label htmlFor="upgrade-coupon-code">Coupon code</label>
              <div>
                <input
                  id="upgrade-coupon-code"
                  value={couponCode}
                  onChange={(event) => setCouponCode(event.target.value)}
                  placeholder="FRIENDS50"
                  autoComplete="off"
                />
                <button type="submit" disabled={couponRedeeming}>
                  {couponRedeeming ? 'Applying...' : 'Apply'}
                </button>
              </div>
              {couponMessage && <small role="status">{couponMessage}</small>}
            </form>
            <button
              className="upgrade-confirm-button"
              type="button"
              onClick={() => {
                setActiveTierId(selectedUpgradeTier.id);
                setUpgradeOpen(false);
                setPlantLimitMessage('');
                saveUserTier(userId, selectedUpgradeTier.id)
                  .then(() => setUpgradeMessage(`${selectedUpgradeTier.name} tier enabled for web testing. No payment was taken.`))
                  .catch(() => setUpgradeMessage(`${selectedUpgradeTier.name} tier enabled locally. Cloud tier sync could not complete yet.`));
              }}
            >
              {selectedUpgradeTier.id === activeTierId ? 'Keep Current Tier' : `Choose ${selectedUpgradeTier.name}`}
            </button>
          </aside>
        </div>
      )}
      {successCelebration && <SuccessModal celebration={successCelebration} onClose={() => setSuccessCelebration(null)} />}
    </main>
  );
}

export default ChloromanceTracker;
